import React from 'react';
import './LessonFooter.css';

/**
 * LessonFooter
 * ------------
 * Sticky footer for the lesson reader (mockup 1c, lines 696-707): a
 * translucent, blurred bar above the content with a progress readout on
 * the left and navigation/completion actions on the right.
 *
 * This replaces the old inline "Mark Completed" button that used to live
 * at the bottom of the content flow. All four of its states are preserved
 * exactly — only the location and styling changed:
 *   - not viewed            -> "Mark lesson complete"
 *   - marking (in flight)   -> "Marking…" (disabled)
 *   - viewed, has next      -> check + "Next"
 *   - viewed, no next       -> "Completed" (disabled)
 *
 * Purely presentational — all branching (what the click does, whether it's
 * disabled) is computed by the caller and passed in as props.
 */
export default function LessonFooter({
  percentComplete = 0,
  currentIndex,
  totalSections = 0,
  totalQuizzes = 0,
  quizzesPassed = 0,
  onPrev,
  prevDisabled = true,
  showPrimaryAction = false,
  primaryLabel,
  primaryDisabled = false,
  primaryBusy = false,
  primaryShowCheck = false,
  primaryTitle,
  onPrimaryAction,
}) {
  const indexLabel = currentIndex != null ? currentIndex : '—';
  const countsLine = totalQuizzes > 0
    ? `${indexLabel} of ${totalSections} lessons · ${quizzesPassed} of ${totalQuizzes} quizzes`
    : `${indexLabel} of ${totalSections} lessons`;

  return (
    <footer className="lesson-footer">
      <div className="lesson-footer-progress">
        <div className="lesson-footer-bar" role="progressbar" aria-valuenow={percentComplete} aria-valuemin={0} aria-valuemax={100} aria-label="Course progress">
          <div className="lesson-footer-bar-fill" style={{ width: `${percentComplete}%` }} />
        </div>
        <span className="lesson-footer-counts">{countsLine}</span>
      </div>

      <div className="lesson-footer-actions">
        <button
          type="button"
          className="secondary-button"
          onClick={onPrev}
          disabled={prevDisabled}
          aria-disabled={prevDisabled}
          aria-label="Previous lesson"
          title="Previous lesson"
        >
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M9.6 3.6L5.2 8l4.4 4.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Previous
        </button>

        {showPrimaryAction && (
          <button
            type="button"
            className="cta-button lesson-footer-primary"
            onClick={onPrimaryAction}
            disabled={primaryDisabled}
            aria-disabled={primaryDisabled}
            title={primaryTitle}
          >
            {primaryShowCheck && (
              <span className="lesson-footer-check">
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M3.4 8.4l3 3 6.2-6.6" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            )}
            {primaryLabel}
          </button>
        )}
      </div>
    </footer>
  );
}
